import {Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {NgbNav, NgbNavItem, NgbNavLinkBase} from "@ng-bootstrap/ng-bootstrap";
import {NavBarItem} from "./nav-bar.item";

import {TranslatePipe} from "@ngx-translate/core";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {isBlank, isDefined, nonBlank, notDefined, unsubscribe} from "../utils/epic.utils";
import {UrlService} from "../../services/url.service";
import {distinctUntilChanged, Subscription} from "rxjs";
import {filter} from "rxjs/operators";

@Component({
  selector: 'acc-nav-bar',
  imports: [
    NgbNav,
    NgbNavItem,
    NgbNavLinkBase,
    TranslatePipe
  ],
  templateUrl: './nav-bar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './nav-bar.component.css'
})
export class NavBarComponent implements OnInit, OnDestroy, OnChanges {

  @Input({required: true})
  public items!: NavBarItem[];

  @Input()
  public selected: NavBarItem | undefined;

  @Input()
  public disabled: boolean = false;

  @Input()
  public autoUpdateUrlHistory: boolean = true;

  @Output()
  public accChange = new EventEmitter<NavBarItem>();

  private readonly subs: Subscription[] = [];
  private navBarParam: string | undefined; // URL param

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly urlService = inject(UrlService);

  constructor() {
    this.subs.push(this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), distinctUntilChanged())
      .subscribe(() => {
        const pathNav = this.getValueFromRoute('nav');
        if (this.navBarParam !== pathNav && nonBlank(pathNav)) {
          this.selected = undefined;
          this.navBarParam = pathNav;
          this.initSelected();
        }
      }));
  }

  ngOnInit(): void {
    this.navBarParam = this.getValueFromRoute('nav');
    this.initSelected();
  }

  ngOnChanges(): void {
    this.navBarParam = this.getValueFromRoute('nav');
    this.initSelected();
  }

  ngOnDestroy(): void {
    this.selected = undefined;
    this.navBarParam = undefined;
    unsubscribe(this.subs);
  }

  protected hasItems(): boolean {
    return isDefined(this.items) && this.items.length > 0;
  }

  public isSelected(id: string): boolean {
    return isDefined(this.selected) && this.selected.id === id;
  }

  protected selectItem(item: NavBarItem): void {
    this.selected = item;
    this.updateUrlHistory(item);
    this.accChange.emit(item);
  }

  private initSelected(): void {
    if (notDefined(this.selected) && this.hasItems()) {
      const selected = this.items.find((i) => i.id === this.navBarParam);
      this.selectItem(selected ? selected : this.items[0]);
    }
  }

  private getValueFromRoute(field: string): string | undefined {
    const value = this.route.snapshot.paramMap.get(field);
    return isDefined(value) && nonBlank(value) ? value : undefined;
  }

  private updateUrlHistory(item: NavBarItem): void {
    if (this.autoUpdateUrlHistory) {
      const path = this.urlService.getCurrentUrl();
      if (nonBlank(path)) {
        const match = path.match(/(;nav=\w+)(?=;|$)/g);
        if (isDefined(match) && nonBlank(match[0]) && match[0] !== ';nav=' + item.id) {
          const history = path.replace(match[0], ';nav=' + item.id);
          this.urlService.updateUrlHistory(history);
        } else if (!match || isBlank(match[0])) {
          const history = path + ';nav=' + item.id;
          this.urlService.updateUrlHistory(history);
        }
      }
    }
  }
}
